import type { JSX } from 'react';

import {
  AlertTriangle,
  Database,
  Key,
  Monitor,
  RefreshCw,
  Server,
  Terminal,
  Trash2,
} from 'lucide-react';

import type { AuthUser, TenantInfo } from '../../hooks/useSetupStatus';
import { frontendEnvForDisplay, isLocal, isNotProduction } from '../../utils/env';

import styles from './SetupRequired.module.css';

interface SetupRequiredProps {
  hasUsers: boolean;
  hasTenants: boolean;
  users: AuthUser[];
  tenants: TenantInfo[];
  environment?: string;
}

const commands = {
  setupTenant: isLocal ? 'pnpm setup-tenant' : 'node scripts/setup-tenant.js',
  updateMapping: isLocal ? 'pnpm update-mapping' : 'node scripts/update-user-tenant-mapping.js',
  deleteTenantData: isLocal ? 'pnpm delete-tenant-data' : 'node scripts/delete-tenant-data.js',
};

export function SetupRequired({
  hasUsers,
  hasTenants,
  users,
  tenants,
  environment,
}: SetupRequiredProps): JSX.Element {
  // Determine which scenario we're in:
  // 1. Has user but no tenant → needs update-mapping (link existing user to tenant)
  // 2. No user and no tenant → needs setup-tenant (fresh setup)
  // 3. Has tenant but no user → orphaned data, needs delete-tenant-data then setup-tenant
  const needsUpdateMapping = hasUsers && !hasTenants;
  const needsFreshSetup = !hasUsers && !hasTenants;
  const hasOrphanedTenant = !hasUsers && hasTenants;

  const getDescription = () => {
    if (needsUpdateMapping) {
      return 'Your user exists but is not linked to a tenant. Run the update mapping script to connect your user to an existing tenant.';
    }
    if (hasOrphanedTenant) {
      return isNotProduction
        ? 'Tenant data exists but there are no users. You can either delete the orphaned tenant data and start fresh, or set BOOTSTRAP_ADMIN_EMAIL and restart.'
        : 'Tenant data exists but there are no users. Set BOOTSTRAP_ADMIN_EMAIL and restart the server, or sign up via the app.';
    }
    return 'Revvly is not yet configured. Run the setup script to create your first tenant and user.';
  };

  return (
    <div className={styles.Container}>
      <div className={styles.Card}>
        <div className={styles.IconContainer}>
          <AlertTriangle className={styles.Icon} />
        </div>

        <h1 className={styles.Title}>Setup Required</h1>

        <p className={styles.Description}>{getDescription()}</p>

        <div className={styles.EnvironmentBadges}>
          {environment && (
            <div className={styles.EnvironmentBadge}>
              <Server className={styles.EnvironmentIcon} />
              <span>Backend: </span>
              <strong>{environment}</strong>
            </div>
          )}
          {frontendEnvForDisplay && (
            <div className={styles.EnvironmentBadge}>
              <Monitor className={styles.EnvironmentIcon} />
              <span>Frontend: </span>
              <strong>{frontendEnvForDisplay}</strong>
            </div>
          )}
        </div>

        <div className={styles.StatusList}>
          <div className={styles.StatusItem}>
            <Key className={styles.StatusIcon} />
            <span className={styles.StatusLabel}>Auth Users</span>
            <span className={hasUsers ? styles.StatusOk : styles.StatusMissing}>
              {hasUsers ? `✓ ${users.length} found` : '✗ Not found'}
            </span>
          </div>
          {users.length > 0 && (
            <ul className={styles.DetailList}>
              {users.map((user) => (
                <li key={user.email} className={styles.DetailItem}>
                  <span className={styles.DetailPrimary}>{user.email}</span>
                  {user.name && (
                    <span className={styles.DetailSecondary}>{user.name}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className={styles.StatusItem}>
            <Database className={styles.StatusIcon} />
            <span className={styles.StatusLabel}>Tenant Data</span>
            <span className={hasTenants ? styles.StatusOk : styles.StatusMissing}>
              {hasTenants ? `✓ ${tenants.length} found` : '✗ Not found'}
            </span>
          </div>
          {tenants.length > 0 && (
            <ul className={styles.DetailList}>
              {tenants.map((tenant) => (
                <li key={tenant.slug} className={styles.DetailItem}>
                  <span className={styles.DetailPrimary}>{tenant.name}</span>
                  <span className={styles.DetailSecondary}>
                    {tenant.slug}
                    {!tenant.isActive && ' (inactive)'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Scenario 1: User exists, no tenant → update-mapping */}
        {needsUpdateMapping && (
          <div className={styles.Instructions}>
            <h2 className={styles.InstructionsTitle}>
              <RefreshCw className={styles.TerminalIcon} />
              Link Your User to a Tenant
            </h2>

            <p className={styles.InstructionsSubtitle}>
              Your user needs to be mapped to an existing tenant.
            </p>

            <div className={styles.CodeBlock}>
              <code>{commands.updateMapping}</code>
            </div>

            <p className={styles.InstructionsText}>This script will:</p>

            <ul className={styles.InstructionsList}>
              <li>Find your user account</li>
              <li>Link it to an existing tenant</li>
              <li>Set up the proper permissions</li>
            </ul>
          </div>
        )}

        {/* Scenario 2: Nothing exists → setup-tenant */}
        {needsFreshSetup && (
          <div className={styles.Instructions}>
            <h2 className={styles.InstructionsTitle}>
              <Terminal className={styles.TerminalIcon} />
              Run the Setup Script
            </h2>

            <p className={styles.InstructionsSubtitle}>
              Create your first tenant and admin user from scratch.
            </p>

            <div className={styles.CodeBlock}>
              <code>{commands.setupTenant}</code>
            </div>

            <p className={styles.InstructionsText}>This script will guide you through creating:</p>

            <ul className={styles.InstructionsList}>
              <li>A new tenant (organization)</li>
              <li>Your admin user account</li>
              <li>The connection between your user and tenant</li>
            </ul>
          </div>
        )}

        {/* Scenario 3: Tenant exists but no user */}
        {hasOrphanedTenant && (
          <div className={styles.InstructionsGroup}>
            {/* Delete option in local/dev/staging — never in production */}
            {isNotProduction && (
              <>
                <div className={styles.InstructionsDanger}>
                  <h2 className={styles.InstructionsTitle}>
                    <Trash2 className={styles.TerminalIconDanger} />
                    Option 1: Clear All Data
                  </h2>

                  <p className={styles.InstructionsSubtitle}>
                    Delete all database collections and start completely fresh.
                  </p>

                  <div className={styles.CodeBlockDanger}>
                    <code>{commands.deleteTenantData}</code>
                  </div>

                  <p className={styles.InstructionsText}>Then run the setup script:</p>

                  <div className={styles.CodeBlock}>
                    <code>{commands.setupTenant}</code>
                  </div>
                </div>

                <div className={styles.Divider}>
                  <span>or</span>
                </div>
              </>
            )}

            <div className={styles.Instructions}>
              <h2 className={styles.InstructionsTitle}>
                <Key className={styles.TerminalIcon} />
                {isNotProduction ? 'Option 2: Set Up Admin User' : 'Set Up Admin User'}
              </h2>

              <p className={styles.InstructionsSubtitle}>
                Set BOOTSTRAP_ADMIN_EMAIL in your environment and restart the server, or sign up via the app.
              </p>
            </div>
          </div>
        )}

        <div className={styles.RefreshSection}>
          <button
            type="button"
            className={styles.RefreshButton}
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
          <p className={styles.RefreshHint}>After running the script, click refresh</p>
        </div>
      </div>
    </div>
  );
}

export default SetupRequired;
